import { useCallback } from 'react';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import UserIcon from '@sdk/assets/svg/user-blue.svg';
import PickerPersonaItem from '@sdk/components/forms/PickerPersonaItem';

import MergeModal from '@web/components/MergeModal/MergeModal';
import type { MergeModalItem } from '@web/components/MergeModal/mergeModal.types';

type Props = {
  isOpen: boolean;
  fromUsers: MergeModalItem[];
  availableUsers: MergeModalItem[];
  onClose: () => void;
  onSubmit: () => void;
};

const UserMergeModal = ({ isOpen, fromUsers, availableUsers, onClose, onSubmit }: Props) => {
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();

  const handleSubmit = useCallback(
    async (fromItems: MergeModalItem[], targetItem: MergeModalItem) => {
      try {
        await api.users.merge(fromItems, targetItem);
        onSubmit();
        showSuccessToast(gettext('Users merged'));
      } catch (e) {
        showErrorToast(getErrorForToast(e));
      }
    },
    [api.users, onSubmit, showErrorToast, showSuccessToast]
  );

  return (
    <>
      <MergeModal
        isOpen={isOpen}
        fromItems={fromUsers}
        toItems={availableUsers}
        onClose={onClose}
        onSubmit={handleSubmit}
        placeholder={gettext('Select employee')}
        getModalTitle={(isMultiple) => gettext(isMultiple ? 'Merge employees' : 'Merge employee')}
        getHeader={(isMultiple) => gettext(isMultiple ? 'Selected employees' : 'Selected employee')}
        getDescription={(isMultiple) =>
          gettext(
            isMultiple
              ? 'Please specify which employee the selected employees need to be merged into:'
              : 'Please specify which employee the selected employee needs to be merged into:'
          )
        }
        getConfirmation={(isMultiple) =>
          isMultiple
            ? gettext(
                'All shifts, tasks, conversations and pages associated with [[items]] and [[lastItem]] will be reassigned to [[target]]'
              )
            : gettext(
                'All shifts, tasks, conversations and pages associated with [[lastItem]] will be reassigned to [[target]]'
              )
        }
        iconComponent={UserIcon}
        pickerItemComponent={PickerPersonaItem}
      />
      <ToastComponent />
    </>
  );
};

export default UserMergeModal;
